/**
 * @fileoverview WAQ bootstrap: registers processors and starts the drainer.
 */

import { registerProcessor, startDrainer, enqueue as waqEnqueue } from './queue';
import * as taskService from '../services/taskService';
import logger from '../../utils/logger';
import { env } from '../../config/env';
import { enqueueWebhook } from '../webhooks/outbox';
import { supabase } from '../../config/supabase';

/**
 * Register all job processors.
 */
function registerProcessors() {
  // Generic webhook enqueue: inserts into public.webhook_outbox via client
  registerProcessor('webhook.enqueue', async (job) => {
    const { data } = await supabase.auth.getUser();
    const sessionUid = data?.user?.id || undefined;

    // Support older queued jobs that may not have userId/eventType
    const entityId = job?.entityId;
    const payload = job?.payload || {};
    const eventType = String(payload?.eventType || 'event');
    const uid = String(job?.userId || sessionUid || '');

    // If we still don't have a user, drop or retry next pass
    if (!uid) {
      throw new Error('webhook.enqueue: missing user (no session)');
    }

    // Enforce RLS: the authenticated session must match the user we write for
    if (!sessionUid || String(sessionUid) !== String(uid)) {
      throw new Error('webhook.enqueue: no matching auth session');
    }

    const res = await enqueueWebhook({
      userId: uid,
      eventType,
      payload: payload?.body || {},
      entityRef: entityId ? `task:${entityId}` : undefined,
      idempotencyKey: payload?.idempotencyKey,
      meta: payload?.meta || {},
    });
    if (!res) throw new Error('webhook.enqueue: insert failed');
  });

  // Task toggle completion
  registerProcessor('task.toggle', async (job) => {
    const { userId, entityId, idempotencyKey, payload } = job;
    if (!userId || !entityId) throw new Error('Missing userId or entityId');
    const desired = !!(payload && payload.completed);
    logger.info('WAQ processor: task.toggle', { entityId, desired });
    await taskService.updateTask(userId, String(entityId), {
      completed: desired,
      // Persist idempotency for dedupe on retries (partial unique index in DB)
      last_idempotency_key: idempotencyKey,
    });
    // Fire webhook event (best-effort) after success
    try {
      await waqEnqueue({
        userId,
        type: 'webhook.enqueue',
        entity: 'task',
        entityId: String(entityId),
        payload: {
          eventType: 'task.toggled',
          body: { id: String(entityId), userId, completed: desired },
          endpoint: env.n8nFrontendWebhookUrl || env.n8nWebhookUrl,
          meta: { source: 'waq', kind: 'toggle' },
        },
      });
    } catch (_) {
      // ignore
    }
  });

  // Task soft delete
  registerProcessor('task.delete', async (job) => {
    const { userId, entityId, idempotencyKey } = job;
    if (!userId || !entityId) throw new Error('Missing userId or entityId');
    logger.info('WAQ processor: task.delete', { entityId });
    await taskService.updateTask(userId, String(entityId), {
      deleted_at: new Date().toISOString(),
      last_idempotency_key: idempotencyKey,
    });
    // Webhook
    try {
      await waqEnqueue({
        userId,
        type: 'webhook.enqueue',
        entity: 'task',
        entityId: String(entityId),
        payload: {
          eventType: 'task.deleted',
          body: { id: String(entityId), userId },
          endpoint: env.n8nFrontendWebhookUrl || env.n8nWebhookUrl,
          meta: { source: 'waq', kind: 'delete' },
        },
      });
    } catch (e) {
      // Non-blocking: webhook enqueue best-effort
      logger.warn('WAQ webhook enqueue (delete) failed', /** @type {any} */ (e));
    }
  });

  // Task create (best-effort offline path)
  registerProcessor('task.create', async (job) => {
    const { userId, payload, idempotencyKey } = job;
    if (!userId) throw new Error('Missing userId');
    logger.info('WAQ processor: task.create');
    const body = { ...(payload || {}), user_id: userId, last_idempotency_key: idempotencyKey };
    const created = await taskService.createTask(userId, body);
    // Webhook
    try {
      await waqEnqueue({
        userId,
        type: 'webhook.enqueue',
        entity: 'task',
        entityId: String(created?.id || payload?.id || ''),
        payload: {
          eventType: 'task.created',
          body: { ...(created || body), id: String(created?.id || payload?.id || '') },
          endpoint: env.n8nFrontendWebhookUrl || env.n8nWebhookUrl,
          meta: { source: 'waq', kind: 'create' },
        },
      });
    } catch (e) {
      // Non-blocking: webhook enqueue best-effort
      logger.warn('WAQ webhook enqueue (create) failed', /** @type {any} */ (e));
    }
  });

  // Task update (title, due_date, description, etc.)
  registerProcessor('task.update', async (job) => {
    const { userId, entityId, idempotencyKey, payload } = job;
    if (!userId || !entityId) throw new Error('Missing userId or entityId');
    logger.info('WAQ processor: task.update', { entityId });
    const updates = { ...(payload || {}), last_idempotency_key: idempotencyKey };
    const updated = await taskService.updateTask(userId, String(entityId), updates);
    // Webhook
    try {
      await waqEnqueue({
        userId,
        type: 'webhook.enqueue',
        entity: 'task',
        entityId: String(entityId),
        payload: {
          eventType: 'task.updated',
          body: { ...(updated || updates), id: String(entityId) },
          endpoint: env.n8nFrontendWebhookUrl || env.n8nWebhookUrl,
          meta: { source: 'waq', kind: 'update' },
        },
      });
    } catch (e) {
      // Non-blocking: webhook enqueue best-effort
      logger.warn('WAQ webhook enqueue (update) failed', /** @type {any} */ (e));
    }
  });
}

/**
 * Initialize WAQ in the foreground app.
 */
export function initWaq() {
  try {
    registerProcessors();
    startDrainer();
  } catch (e) {
    logger.warn('WAQ init failed (non-blocking):', /** @type {any} */ (e));
  }
}

export default { initWaq };
